import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Gift, Sparkles, Copy, Star, Trophy, History, Clock, CheckCircle2, RotateCcw, QrCode, Lock } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import RaffleCustomerCard from "../components/RaffleCustomerCard";

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customer/me");
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const copyCode = () => {
    navigator.clipboard.writeText(user?.whatsapp || "");
    toast.success("Celular copiado — compártelo con el comercio para que te otorgue puntos");
  };

  const formatWA = (w) => {
    if (!w) return "—";
    const d = String(w).replace(/\D/g, "");
    if (d.length === 10) return `${d.slice(0, 2)} ${d.slice(2, 6)} ${d.slice(6)}`;
    return w;
  };

  const requestRedeem = async (businessId, businessName) => {
    setRedeeming(businessId);
    try {
      await api.post("/customer/redeem", { business_id: businessId });
      toast.success(`Canje solicitado en ${businessName}. Muestra esta pantalla en sucursal.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al canjear");
    } finally { setRedeeming(null); }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-background"><Navbar />
        <div className="max-w-7xl mx-auto px-6 py-20 text-muted-foreground">Cargando tu cuenta…</div>
      </div>
    );
  }

  const { balances, transactions, redemptions, stats } = data;
  const pendingRedemptions = redemptions.filter((r) => r.status === "pending");

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="overline text-[#7C3AED]">Mi cuenta de cliente</div>
            <h1 className="font-display text-4xl tracking-tight mt-2">Hola, {user?.name || "cliente"} 👋</h1>
            <p className="text-muted-foreground text-sm mt-1">Acumula puntos en cada compra y canjea tus recompensas.</p>
          </div>
          <div className="bg-gradient-to-r from-[#EDE9FE] to-[#F5F3FF] border border-[#C4B5FD] rounded-xl p-4 flex items-center gap-4">
            <div className="flex-1">
              <div className="overline text-[#5B21B6] text-[10px]">Tu código de cliente (celular)</div>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-mono text-sm font-semibold text-[#0E0E12]">{formatWA(user?.whatsapp)}</span>
                <button onClick={copyCode} className="p-1 hover:bg-white/60 rounded" data-testid="copy-code-btn" title="Copiar">
                  <Copy className="w-3.5 h-3.5 text-[#5B21B6]" />
                </button>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">O muestra tu QR en sucursal</div>
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <button className="bg-white border border-[#C4B5FD] rounded-lg p-2 hover:shadow-md transition-shadow" title="Ver QR grande" data-testid="qr-show-btn">
                  <div className="bg-white p-1 rounded">
                    <QRCodeSVG value={user?.whatsapp || ""} size={56} level="M" includeMargin={false} />
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-sm" data-testid="qr-modal">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><QrCode className="w-5 h-5 text-[#7C3AED]" /> Tu código QR</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center py-2">
                  <div className="bg-white p-4 rounded-2xl border-2 border-[#C4B5FD] shadow-lg">
                    <QRCodeSVG value={user?.whatsapp || ""} size={240} level="H" includeMargin={false} />
                  </div>
                  <div className="mt-4 text-center">
                    <div className="font-display text-2xl">{user?.name}</div>
                    <div className="font-mono text-sm text-muted-foreground mt-1">{formatWA(user?.whatsapp)}</div>
                  </div>
                  <p className="mt-4 text-xs text-center text-muted-foreground max-w-xs">
                    Muestra este código al comercio. Lo escanea y te otorga tus puntos al instante — no tienes que dictar tu celular.
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mt-8">
          <StatBox icon={<Star className="w-5 h-5" />} label="Puntos ganados" value={stats.total_earned} />
          <StatBox icon={<Trophy className="w-5 h-5" />} label="Puntos canjeados" value={stats.total_redeemed} />
          <StatBox icon={<Gift className="w-5 h-5" />} label="Comercios" value={stats.businesses_count} />
        </div>

        {/* Monthly raffle */}
        <div className="mt-8">
          <RaffleCustomerCard />
        </div>

        {/* Pending redemption banner */}
        {pendingRedemptions.length > 0 && (
          <div className="mt-8 bg-gradient-to-r from-[#7C3AED] to-[#5B21B6] text-white rounded-2xl p-6 relative overflow-hidden" data-testid="pending-redemptions-banner">
            <div className="overline text-purple-200">Canjes pendientes de entrega</div>
            <div className="font-display text-2xl mt-2">Muestra esta pantalla en sucursal</div>
            <div className="mt-4 space-y-2">
              {pendingRedemptions.map((r) => (
                <div key={r.id} className="bg-white/15 backdrop-blur rounded-lg p-3 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-semibold">{r.business_name}</div>
                    <div className="text-sm text-purple-100">🎁 {r.reward}</div>
                  </div>
                  <Badge className="bg-white/25 text-white"><Clock className="w-3 h-3 mr-1" />Esperando</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Balances per business */}
        <div className="mt-10">
          <h2 className="font-display text-2xl tracking-tight">Mis puntos por comercio</h2>
          {balances.length === 0 ? (
            <div className="bg-white border border-dashed border-border rounded-2xl p-10 text-center mt-4">
              <Gift className="w-10 h-10 mx-auto text-[#7C3AED]" />
              <div className="font-display text-xl mt-3">Aún no tienes puntos</div>
              <p className="text-muted-foreground text-sm mt-2 max-w-md mx-auto">
                Visita los comercios del directorio y muéstrales tu correo. Ellos te otorgarán puntos por tus compras.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5 mt-5 stagger">
              {balances.map((b) => {
                const threshold = b.loyalty_threshold || 0;
                const balance = b.points_balance || 0;
                const pct = threshold > 0 ? Math.min(100, Math.round((balance / threshold) * 100)) : 0;
                const ready = threshold > 0 && balance >= threshold;
                return (
                  <div key={b.business_id} className="bg-white border border-[#C4B5FD] rounded-2xl p-6 hover:shadow-md transition-shadow" data-testid={`balance-${b.business_id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-xl bg-[#F5F3FF] grid place-items-center overflow-hidden border border-border shrink-0">
                        {b.logo_base64 ? <img src={b.logo_base64} alt="" className="w-full h-full object-cover" /> : <Gift className="w-5 h-5 text-[#7C3AED]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="overline text-muted-foreground">{b.giro}</div>
                        <h3 className="font-display text-xl truncate">{b.business_name}</h3>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-3xl text-[#7C3AED]">{balance}</div>
                        <div className="text-[10px] text-muted-foreground overline">puntos</div>
                      </div>
                    </div>

                    {threshold > 0 && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between text-xs mb-1.5">
                          <span className="text-muted-foreground">Progreso a recompensa</span>
                          <span className="font-medium text-[#5B21B6]">{balance} / {threshold}</span>
                        </div>
                        <Progress value={pct} className="h-2" />
                        <div className="mt-3 text-sm">
                          <span className="text-muted-foreground">Recompensa: </span>
                          <span className="font-medium text-[#0E0E12]">🎁 {b.loyalty_reward || "—"}</span>
                        </div>
                      </div>
                    )}

                    <div className="mt-5 flex items-center justify-between gap-3">
                      <div className="text-xs text-muted-foreground">
                        Ganados: <strong>{b.total_earned || 0}</strong> · Canjeados: <strong>{b.total_redeemed || 0}</strong>
                      </div>
                      {ready ? (
                        <Button
                          className="btn-primary rounded-full px-4 h-9 text-sm"
                          onClick={() => requestRedeem(b.business_id, b.business_name)}
                          disabled={redeeming === b.business_id}
                          data-testid={`redeem-btn-${b.business_id}`}
                        >
                          {redeeming === b.business_id ? "Canjeando…" : "Canjear ahora"}
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">Faltan {Math.max(0, threshold - balance)} pts</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* History */}
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <div className="bg-white border border-border rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-[#7C3AED]" />
              <h3 className="font-display text-xl">Movimientos recientes</h3>
            </div>
            {transactions.length === 0 ? (
              <div className="text-sm text-muted-foreground">Aún no hay movimientos.</div>
            ) : (
              <ul className="divide-y divide-border">
                {transactions.slice(0, 12).map((t) => (
                  <li key={t.id} className="py-3 flex items-center justify-between" data-testid={`txn-${t.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{t.business_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(t.created_at).toLocaleString("es-MX")} {t.note ? `· ${t.note}` : ""}
                      </div>
                    </div>
                    <div className={`font-mono text-sm font-semibold ${t.points >= 0 ? "text-[#5B21B6]" : "text-zinc-700"}`}>
                      {t.points > 0 ? `+${t.points}` : t.points} pts
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-white border border-border rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Gift className="w-5 h-5 text-[#7C3AED]" />
              <h3 className="font-display text-xl">Mis canjes</h3>
            </div>
            {redemptions.length === 0 ? (
              <div className="text-sm text-muted-foreground">Aún no has canjeado.</div>
            ) : (
              <ul className="divide-y divide-border">
                {redemptions.map((r) => (
                  <li key={r.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{r.business_name}</div>
                      <div className="text-xs text-muted-foreground">{r.reward} · {new Date(r.created_at).toLocaleDateString("es-MX")}</div>
                    </div>
                    <RedemptionBadge status={r.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <CustomerChangePassword />
      </section>
      <Footer />
    </div>
  );
}

function CustomerChangePassword() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (form.new_password !== form.confirm) { toast.error("Las contraseñas no coinciden"); return; }
    if (form.new_password.length < 6) { toast.error("Mínimo 6 caracteres"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Contraseña actualizada");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar contraseña");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-8 bg-white border border-border rounded-2xl p-6 max-w-md space-y-4" data-testid="customer-change-password">
      <div className="flex items-center gap-2">
        <Lock className="w-4 h-4 text-muted-foreground" />
        <h3 className="font-semibold text-lg">Cambiar contraseña</h3>
      </div>
      <div><Label className="text-sm">Contraseña actual</Label><Input type="password" className="mt-1.5" value={form.current_password} onChange={(e) => set("current_password", e.target.value)} data-testid="customer-pwd-current" /></div>
      <div><Label className="text-sm">Nueva contraseña</Label><Input type="password" className="mt-1.5" value={form.new_password} onChange={(e) => set("new_password", e.target.value)} data-testid="customer-pwd-new" /></div>
      <div><Label className="text-sm">Confirmar nueva contraseña</Label><Input type="password" className="mt-1.5" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} data-testid="customer-pwd-confirm" /></div>
      <Button onClick={save} disabled={busy || !form.current_password || !form.new_password} className="rounded-full px-6" data-testid="customer-pwd-save-btn">
        {busy ? "Guardando…" : "Cambiar contraseña"}
      </Button>
    </div>
  );
}

const StatBox = ({ icon, label, value }) => (
  <div className="bg-white border border-[#C4B5FD] rounded-2xl p-5">
    <div className="flex items-center gap-2 text-[#7C3AED]">{icon}<span className="overline text-[10px] text-muted-foreground">{label}</span></div>
    <div className="font-display text-3xl mt-2">{value}</div>
  </div>
);

const RedemptionBadge = ({ status }) => {
  if (status === "claimed") return <Badge className="bg-[#7C3AED] text-white"><CheckCircle2 className="w-3 h-3 mr-1" />Entregado</Badge>;
  if (status === "refunded") return <Badge variant="outline"><RotateCcw className="w-3 h-3 mr-1" />Devuelto</Badge>;
  return <Badge className="bg-zinc-200 text-zinc-900"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>;
};
